import React from 'react';
import { useBulkUpload } from '../../hooks/useBulkUpload';
import { reset } from '../../context/bulkUploadActions';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Chip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
  },
  successIcon: {
    fontSize: 80,
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 80,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

export const ResultsStep = () => {
  const classes = useStyles();
  const { state, dispatch } = useBulkUpload();
  const { results } = state;

  const handleUploadAnother = () => {
    dispatch(reset());
  };

  const hasFailures = results?.failed && results.failed.length > 0;

  return (
    <Box className={classes.container}>
      {/* Success Header */}
      <Box textAlign="center" mb={6}>
        {!hasFailures ? (
          <>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h4" gutterBottom>
              Upload Complete!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              All students have been successfully enrolled in the program.
            </Typography>
          </>
        ) : (
          <>
            <ErrorIcon className={classes.errorIcon} />
            <Typography variant="h4" gutterBottom>
              Upload Completed with Errors
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Some students could not be processed. Please review the errors below.
            </Typography>
          </>
        )}
      </Box>

      {/* Statistics */}
      <div className="row mb-6">
        <div className="col-md-4">
          <Box className={classes.statsCard} style={{ backgroundColor: '#e8f5e9' }}>
            <Typography variant="h2" style={{ color: '#388e3c', fontWeight: 700 }}>
              {results?.successCount || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
              SUCCESSFULLY ENROLLED
            </Typography>
          </Box>
        </div>
        
        {hasFailures && (
          <div className="col-md-4">
            <Box className={classes.statsCard} style={{ backgroundColor: '#ffebee' }}>
              <Typography variant="h2" style={{ color: '#d32f2f', fontWeight: 700 }}>
                {results?.failedCount || 0}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
                FAILED
              </Typography>
            </Box>
          </div>
        )}
        
        <div className="col-md-4">
          <Box className={classes.statsCard} style={{ backgroundColor: '#e3f2fd' }}>
            <Typography variant="h2" style={{ color: '#1976d2', fontWeight: 700 }}>
              {results?.totalProcessed || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ fontWeight: 600 }}>
              TOTAL PROCESSED
            </Typography>
          </Box>
        </div>
      </div>

      {/* Success List */}
      {results?.success && results.success.length > 0 && (
        <div className="card card-custom mb-6">
          <div className="card-header">
            <div className="card-title">
              <h3 className="card-label">
                <i className="fa fa-check-circle text-success mr-2"></i>
                Successfully Enrolled Students
              </h3>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Student Name</th>
                    <th>Parent(s)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.success.map((record, idx) => (
                    <tr key={idx}>
                      <td>{record.row}</td>
                      <td>{record.studentName}</td>
                      <td>{record.parentNames}</td>
                      <td>
                        <Chip
                          label="Enrolled"
                          size="small"
                          style={{ backgroundColor: '#4caf50', color: 'white' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Failed List */}
      {hasFailures && (
        <div className="card card-custom mb-6">
          <div className="card-header">
            <div className="card-title">
              <h3 className="card-label">
                <i className="fa fa-exclamation-circle text-danger mr-2"></i>
                Failed Enrollments
              </h3>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Student Name</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {results.failed.map((record, idx) => (
                    <tr key={idx}>
                      <td>{record.row}</td>
                      <td>{record.studentName}</td>
                      <td className="text-danger">{record.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="center" gap={2} mt={6}>
        <button
          className="btn btn-light-primary btn-lg"
          onClick={handleUploadAnother}
        >
          <i className="fa fa-upload mr-2"></i>
          Upload Another File
        </button>
      </Box>
    </Box>
  );
};